import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '../client'
import type {
  ArquivoTocado,
  AtividadeResponse,
  AutorNaTela,
  ApoioDoResumo,
  CommitNaTela,
  FiltroDeArquivos,
  FiltrosDeCommits,
  MesComAtividade,
  PaginaDeCommits,
  RepositorioNaTela,
  SetorDoAutor,
  SituacaoDaColeta,
} from '../../types/repositorios'

/** As chaves de cache da atividade nos repositórios. */
export const chavesDeRepositorios = {
  tudo: () => ['repositorios'] as const,
  situacao: () => ['repositorios', 'situacao'] as const,
  meses: () => ['repositorios', 'meses'] as const,
  atividade: (competencia: string) => ['repositorios', 'atividade', competencia] as const,
  commits: (competencia: string, filtros: FiltrosDeCommits) =>
    ['repositorios', 'atividade', competencia, 'commits', filtros] as const,
  arquivos: (competencia: string, filtro: FiltroDeArquivos) =>
    ['repositorios', 'atividade', competencia, 'arquivos', filtro] as const,
  autores: () => ['repositorios', 'autores'] as const,
  repositorios: () => ['repositorios', 'lista'] as const,
  commitsDaGmud: (id: number) => ['gmuds', 'commits', id] as const,
}

/** Se a coleta está disponível, com que credencial, e a última rodada. */
export function useSituacaoDaColeta() {
  return useQuery({
    queryKey: chavesDeRepositorios.situacao(),
    queryFn: async () => (await api.get<SituacaoDaColeta>('/repositorios/situacao')).data,
    // Enquanto há rodada em voo, a tela acompanha; fora disso, uma vez por minuto basta.
    refetchInterval: (consulta) => (consulta.state.data?.emAndamento ? 5_000 : 60_000),
  })
}

/** Os meses com commit coletado, do mais recente para o mais antigo. */
export function useMesesComAtividade() {
  return useQuery({
    queryKey: chavesDeRepositorios.meses(),
    queryFn: async () => (await api.get<MesComAtividade[]>('/repositorios/meses')).data,
  })
}

/**
 * A atividade de um mês até o D-1, com o apoio do resumo.
 *
 * Este GET nunca gera resumo: só lê o gravado. Enquanto o resumo está sendo preparado a consulta
 * se repete sozinha, para o texto aparecer sem recarregar.
 *
 * @param competencia mês no formato `AAAA-MM`
 */
export function useAtividadeDoMes(competencia: string) {
  return useQuery({
    queryKey: chavesDeRepositorios.atividade(competencia),
    queryFn: async () => (await api.get<AtividadeResponse>(`/repositorios/atividade/${competencia}`)).data,
    enabled: competencia.length > 0,
    refetchInterval: (consulta) => (consulta.state.data?.resumo.estado === 'PREPARANDO' ? 5_000 : false),
  })
}

/**
 * Os commits do mês, com filtros e paginação.
 *
 * @param competencia mês
 * @param filtros o recorte
 */
export function useCommitsDoMes(competencia: string, filtros: FiltrosDeCommits) {
  return useQuery({
    queryKey: chavesDeRepositorios.commits(competencia, filtros),
    queryFn: async () =>
      (await api.get<PaginaDeCommits>(`/repositorios/atividade/${competencia}/commits`, { params: filtros })).data,
    enabled: competencia.length > 0,
    placeholderData: (anterior) => anterior,
  })
}

/**
 * Os arquivos tocados no mês, do mais tocado para o menos — o "o que foi modificado" por pessoa
 * ou por repositório. Mesmo recorte D-1 da atividade, e a mesma exclusão da equipe interna: para
 * alguém do Diário de Bordo a lista vem vazia.
 *
 * @param competencia mês
 * @param filtro autor, repositório e limite (100 por padrão, 500 no máximo)
 * @param ligado permite montar o hook antes de a pessoa abrir o diálogo, sem consultar
 */
export function useArquivosDoMes(competencia: string, filtro: FiltroDeArquivos, ligado = true) {
  return useQuery({
    queryKey: chavesDeRepositorios.arquivos(competencia, filtro),
    queryFn: async () =>
      (await api.get<ArquivoTocado[]>(`/repositorios/atividade/${competencia}/arquivos`, { params: filtro })).data,
    enabled: ligado && competencia.length > 0,
  })
}

/** Os commits que citam uma solicitação de mudança. */
export function useCommitsDaGmud(id: number) {
  return useQuery({
    queryKey: chavesDeRepositorios.commitsDaGmud(id),
    queryFn: async () => (await api.get<CommitNaTela[]>(`/gmuds/${id}/commits`)).data,
    enabled: Number.isFinite(id),
    staleTime: 60 * 1000,
  })
}

/** Todos os autores já vistos, com os totais. */
export function useAutoresDeRepositorio() {
  return useQuery({
    queryKey: chavesDeRepositorios.autores(),
    queryFn: async () => (await api.get<AutorNaTela[]>('/repositorios/autores')).data,
  })
}

/** Todos os repositórios conhecidos, observados ou não. */
export function useRepositoriosConhecidos() {
  return useQuery({
    queryKey: chavesDeRepositorios.repositorios(),
    queryFn: async () => (await api.get<RepositorioNaTela[]>('/repositorios/repositorios')).data,
  })
}

/** Pede o resumo do mês agora — o botão do fiscal. Idempotente por conteúdo no servidor. */
export function useGerarResumo(competencia: string) {
  const fila = useQueryClient()
  return useMutation({
    mutationFn: async () => (await api.post<ApoioDoResumo>(`/repositorios/atividade/${competencia}/resumo`)).data,
    onSettled: () => fila.invalidateQueries({ queryKey: chavesDeRepositorios.atividade(competencia) }),
  })
}

/** Fixa o setor de um autor. Invalida tudo: o setor entra em toda tabela e no recorte do resumo. */
export function useFixarSetor() {
  const fila = useQueryClient()
  return useMutation({
    mutationFn: async (pedido: { id: number; setor: SetorDoAutor; nomeDeExibicao?: string; observacao?: string }) => {
      await api.put(`/repositorios/autores/${pedido.id}/setor`, {
        setor: pedido.setor,
        nomeDeExibicao: pedido.nomeDeExibicao || null,
        observacao: pedido.observacao || null,
      })
    },
    onSuccess: () => fila.invalidateQueries({ queryKey: chavesDeRepositorios.tudo() }),
  })
}

/**
 * Marca ou desmarca um autor como equipe interna — o ato do fiscal sobre o casamento com o Diário.
 *
 * Invalida tudo: a pessoa entra ou sai de toda tabela, da lista de commits e do recorte do
 * resumo. O que se fixa aqui a sincronização com o Diário nunca sobrescreve.
 */
export function useDefinirEquipeInterna() {
  const fila = useQueryClient()
  return useMutation({
    mutationFn: async (pedido: { id: number; equipeInterna: boolean; observacao?: string }) =>
      (
        await api.put<AutorNaTela>(`/repositorios/autores/${pedido.id}/equipe-interna`, {
          equipeInterna: pedido.equipeInterna,
          observacao: pedido.observacao || null,
        })
      ).data,
    onSuccess: () => fila.invalidateQueries({ queryKey: chavesDeRepositorios.tudo() }),
  })
}

/** Liga ou desliga um repositório da coleta. */
export function useDefinirObservado() {
  const fila = useQueryClient()
  return useMutation({
    mutationFn: async (pedido: { id: number; observado: boolean }) => {
      await api.put(`/repositorios/repositorios/${pedido.id}/observado`, { observado: pedido.observado })
    },
    onSuccess: () => fila.invalidateQueries({ queryKey: chavesDeRepositorios.tudo() }),
  })
}

/** Coleta os commits agora, em segundo plano. */
export function useColetarAgora() {
  const fila = useQueryClient()
  return useMutation({
    mutationFn: async () => (await api.post<{ iniciada: boolean }>('/repositorios/sincronizacoes')).data,
    onSettled: () => fila.invalidateQueries({ queryKey: chavesDeRepositorios.situacao() }),
  })
}
